import { useState, useCallback, useRef, useEffect } from 'react';

// DiceBox types based on the library's actual API (observed from console output)
interface DiceRollItem {
  value: number;
  sides: number;
  dieType?: string;
  groupId?: number;
  rollId?: number;
  theme?: string;
  themeColor?: string;
}

interface RollResultSet {
  dice?: DiceRollItem[];
  rolls?: Array<{
    rolls?: DiceRollItem[];
    [key: string]: unknown;
  }>;
  total?: number;
}

interface RollResult {
  notation?: string;
  qty?: number;
  modifier?: number;
  sides?: number;
  rolls?: DiceRollItem[];  // Array of individual die results
  sets?: RollResultSet[];
  value?: number;  // DiceBox uses 'value' for the total, not 'total'
  total?: number;  // Keep for backwards compatibility
  id?: number;
}

interface DiceBoxConfig {
  container: string;
  assetPath: string;
  theme?: string;
  themeColor?: string;
  scale?: number;
  gravity?: number;
  throwForce?: number;
  spinForce?: number;
  startingHeight?: number;
  settleTimeout?: number;
  offscreen?: boolean;
  delay?: number;
  lightIntensity?: number;
  enableShadows?: boolean;
}

interface DiceBoxInstance {
  init: () => Promise<void>;
  roll: (notation: string | string[]) => Promise<RollResult[]>;
  add: (notation: string | string[]) => Promise<RollResult[]>;
  clear: () => void;
  hide: () => void;
  show: () => void;
  updateConfig: (config: Partial<DiceBoxConfig>) => void;
  onRollComplete: ((results: RollResult[]) => void) | null;
  onDieComplete: ((result: DiceRollItem) => void) | null;
}

interface UseDiceBoxOptions {
  containerId: string;
  enabled?: boolean;
  onRollComplete?: (results: RollResult[]) => void;
}

interface UseDiceBoxReturn {
  isReady: boolean;
  isRolling: boolean;
  roll: (notation: string | string[]) => Promise<RollResult[] | null>;
  clear: () => void;
  error: Error | null;
}

export function useDiceBox({ containerId, enabled = true, onRollComplete }: UseDiceBoxOptions): UseDiceBoxReturn {
  const [isReady, setIsReady] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const diceBoxRef = useRef<DiceBoxInstance | null>(null);
  const initializingRef = useRef(false);
  const onRollCompleteRef = useRef(onRollComplete);
  onRollCompleteRef.current = onRollComplete;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (initializingRef.current || diceBoxRef.current) {
      return;
    }

    const container = document.querySelector(containerId) as HTMLElement;
    if (!container) {
      console.warn(`DiceBox container ${containerId} not found, waiting...`);
      return;
    }

    const waitForDimensions = (): Promise<void> => {
      return new Promise((resolve) => {
        const check = () => {
          if (container.offsetWidth > 0 && container.offsetHeight > 0) {
            resolve();
          } else {
            requestAnimationFrame(check);
          }
        };
        check();
      });
    };

    const initDiceBox = async () => {
      await waitForDimensions();
      initializingRef.current = true;

      try {
        const DiceBoxModule = await import('@3d-dice/dice-box');
        const DiceBox = DiceBoxModule.default;

        const diceBox = new DiceBox({
          container: containerId,
          assetPath: '/assets/',
          theme: 'default',
          themeColor: '#e85a00',
          scale: 5,
          gravity: 2,
          throwForce: 5,
          spinForce: 4,
          startingHeight: 8,
          settleTimeout: 3000,
          offscreen: false,
          delay: 10,
          lightIntensity: 1.5,
          enableShadows: true,
        }) as DiceBoxInstance;

        await diceBox.init();

        // Force canvas resize
        const containerEl = document.querySelector(containerId) as HTMLElement;
        const canvas = containerEl?.querySelector('canvas');
        if (canvas && containerEl) {
          const width = containerEl.offsetWidth || window.innerWidth;
          const height = containerEl.offsetHeight || window.innerHeight;
          canvas.width = width;
          canvas.height = height;
          canvas.style.width = `${width}px`;
          canvas.style.height = `${height}px`;
          diceBox.updateConfig({});
        }

        // Use ref for stable callback
        diceBox.onRollComplete = (results: RollResult[]) => {
          onRollCompleteRef.current?.(results);
        };

        diceBoxRef.current = diceBox;
        setIsReady(true);
        setError(null);
      } catch (err) {
        console.error('Failed to initialize DiceBox:', err);
        setError(err instanceof Error ? err : new Error('Failed to initialize DiceBox'));
      } finally {
        initializingRef.current = false;
      }
    };

    initDiceBox();

    return () => {
      if (diceBoxRef.current) {
        diceBoxRef.current.clear();
        diceBoxRef.current = null;
      }
      setIsReady(false);
      initializingRef.current = false;
    };
  }, [containerId, enabled]);

  const roll = useCallback(async (notation: string | string[]): Promise<RollResult[] | null> => {
    if (!diceBoxRef.current || !isReady) {
      console.warn('DiceBox not ready');
      return null;
    }

    try {
      setIsRolling(true);
      diceBoxRef.current.clear();
      const results = await diceBoxRef.current.roll(notation);
      return results;
    } catch (err) {
      console.error('Roll failed:', err);
      setError(err instanceof Error ? err : new Error('Roll failed'));
      return null;
    } finally {
      setIsRolling(false);
    }
  }, [isReady]);

  const clear = useCallback(() => {
    if (diceBoxRef.current) {
      diceBoxRef.current.clear();
    }
  }, []);

  return {
    isReady,
    isRolling,
    roll,
    clear,
    error,
  };
}

export type { RollResult, DiceRollItem, DiceBoxConfig };
